import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import {
  ClosingIcon,
  ClosingPill,
  NotaryDesignation,
  NotaryLane,
  NotaryLaneStatus,
  SlotVariant,
  isAlertVariant,
  isCancelledVariant,
} from "./models/ron-monitor.models";

/** The API's business day is Eastern (Package_GetRonMonitorBoard); the board renders in it for every viewer. See ADR-0003. */
export const BOARD_TIME_ZONE = "America/New_York";
export const BOARD_TIME_ZONE_NOTE = "all times ET (Eastern)";
export const SLOT_MINUTES = 20;
export const SLOT_START_HOUR = 8;
export const SLOT_END_HOUR = 23;
export const VISIBLE_SLOTS = 9;
export const MIN_VISIBLE_SLOTS = 4;
export const UNASSIGNED_LANE_ID = "unassigned";
export const UNASSIGNED_LANE_NAME = "Unassigned";
export const NO_VALUE = "—";

const DESIGNATION_LABELS: Readonly<Record<NotaryDesignation, string>> = {
  TeamSigningAgent: "TSA",
  PreferredAgent: "PA",
};

export function designationLabel(
  designation: NotaryDesignation | null
): string | null {
  return designation ? DESIGNATION_LABELS[designation] : null;
}

const MINUTES_PER_HOUR = 60;
const NOON_MINUTES = 12 * MINUTES_PER_HOUR;

export interface SlotColumn {
  index: number;
  startMinutes: number;
  label: string;
  meridiem: "am" | "pm";
}

export type SlotAxis = SlotColumn[];

export interface GridLane {
  laneId: string;
  name: string;
  designation: NotaryDesignation | null;
  status: NotaryLaneStatus | null;
  isUnassigned: boolean;
}

export interface SlotPresentation {
  variant: SlotVariant;
  label: string;
  background: string;
  foreground: string;
  icons: ClosingIcon[];
  iconNames: string[];
  renders: boolean;
}

interface VariantStyle {
  label: string;
  background: string;
  foreground: string;
  icons: ClosingIcon[];
  showsDelay: boolean;
  renders: boolean;
}

const VARIANT_STYLES: Readonly<Record<SlotVariant, VariantStyle>> = {
  completed: {
    label: "Completed",
    background: "var(--nova-gray-50)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  "processing-documents": {
    label: "Finalizing",
    background: "var(--nova-green-50)",
    foreground: "var(--nova-green-700)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  "in-session": {
    label: "In session",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["headphone", "person"],
    showsDelay: false,
    renders: true,
  },
  "pre-sign": {
    label: "In session",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["headphone", "person"],
    showsDelay: false,
    renders: true,
  },
  "check-in": {
    label: "Checking-in",
    background: "var(--nova-green-100)",
    foreground: "var(--nova-green-700)",
    icons: ["person"],
    showsDelay: false,
    renders: true,
  },
  scheduled: {
    label: "Scheduled",
    background: "var(--nova-blue-100)",
    foreground: "var(--nova-blue-800)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  "open-slot": {
    label: "Open Slot",
    background: "var(--nova-gray-100)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  over: {
    label: "Over",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: ["headphone"],
    showsDelay: true,
    renders: true,
  },
  delayed: {
    label: "Delayed",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: [],
    showsDelay: true,
    renders: true,
  },
  stuck: {
    label: "Stuck",
    background: "var(--nova-yellow-200)",
    foreground: "var(--nova-yellow-800)",
    icons: [],
    showsDelay: true,
    renders: true,
  },
  canceled: {
    label: "Canceled",
    background: "var(--nova-gray-200)",
    foreground: "var(--nova-gray-600)",
    icons: [],
    showsDelay: false,
    renders: true,
  },
  empty: {
    label: "",
    background: "transparent",
    foreground: "transparent",
    icons: [],
    showsDelay: false,
    renders: false,
  },
};

const ICON_SPRITE_NAMES: Readonly<Record<ClosingIcon, string>> = {
  headphone: "headset_mic",
  person: "person",
  stamp: "notary",
};

export function iconSpriteName(icon: ClosingIcon): string {
  return ICON_SPRITE_NAMES[icon];
}

export function buildSlotAxis(closings: ClosingPill[] = []): SlotAxis {
  const axis: SlotAxis = [];
  const scheduledSlotStarts = closings
    .map((closing) => minutesFromMidnight(new Date(closing.scheduledDateTime)))
    .filter((minutes) => Number.isFinite(minutes))
    .map(slotStartFor);
  const firstMinutes = Math.min(
    SLOT_START_HOUR * MINUTES_PER_HOUR,
    ...scheduledSlotStarts
  );
  const lastMinutes = Math.max(
    SLOT_END_HOUR * MINUTES_PER_HOUR,
    ...scheduledSlotStarts
  );

  for (
    let startMinutes = firstMinutes;
    startMinutes <= lastMinutes;
    startMinutes += SLOT_MINUTES
  ) {
    axis.push({
      index: axis.length,
      startMinutes,
      label: formatSlotLabel(startMinutes),
      meridiem: startMinutes >= NOON_MINUTES ? "pm" : "am",
    });
  }

  return axis;
}

function formatSlotLabel(startMinutes: number): string {
  const hour24 = Math.floor(startMinutes / MINUTES_PER_HOUR);
  const minute = startMinutes % MINUTES_PER_HOUR;
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${hour12}:${`${minute}`.padStart(2, "0")}`;
}

function slotStartFor(minutes: number): number {
  return minutes - (minutes % SLOT_MINUTES);
}

/** A Date whose local getters read the Eastern wall clock for this instant. */
export function toBoardWallClock(value: Date): Date {
  return toZonedTime(value, BOARD_TIME_ZONE);
}

/** Inverse of toBoardWallClock: local calendar fields reinterpreted as Eastern. */
export function fromBoardWallClock(wallClock: Date): Date {
  return fromZonedTime(wallClock, BOARD_TIME_ZONE);
}

export function formatBoardDate(value: Date): string {
  return formatInTimeZone(value, BOARD_TIME_ZONE, "EEEE, MMMM d, y");
}

export function formatBoardShortTime(value: Date): string {
  return formatInTimeZone(value, BOARD_TIME_ZONE, "h:mm a");
}

export function minutesFromMidnight(value: Date): number {
  const wallClock = toBoardWallClock(value);
  return wallClock.getHours() * MINUTES_PER_HOUR + wallClock.getMinutes();
}

export function slotIndexForMinutes(minutes: number, axis: SlotAxis): number | null {
  if (axis.length === 0) {
    return null;
  }
  const offset = minutes - axis[0].startMinutes;
  if (offset < 0) {
    return null;
  }
  const index = Math.floor(offset / SLOT_MINUTES);
  return index < axis.length ? index : null;
}

export function currentSlotIndex(now: Date, axis: SlotAxis): number | null {
  return slotIndexForMinutes(minutesFromMidnight(now), axis);
}

export function bucketKey(laneId: string, slotIndex: number): string {
  return `${laneId}|${slotIndex}`;
}

export function laneIdForClosing(closing: ClosingPill): string {
  return closing.notaryId ?? UNASSIGNED_LANE_ID;
}

export function bucketClosings(
  closings: ClosingPill[],
  axis: SlotAxis
): Map<string, ClosingPill> {
  const buckets = new Map<string, ClosingPill>();

  closings.forEach((closing) => {
    const index = slotIndexForMinutes(
      minutesFromMidnight(new Date(closing.scheduledDateTime)),
      axis
    );
    if (index === null) {
      return;
    }

    const key = bucketKey(laneIdForClosing(closing), index);
    const existing = buckets.get(key);
    if (!existing || outranks(closing, existing)) {
      buckets.set(key, closing);
    }
  });

  return buckets;
}

function outranks(candidate: ClosingPill, existing: ClosingPill): boolean {
  return candidate.packageId > existing.packageId;
}

/** The axis widens past business hours to show an out-of-hours closing; those slots are still not capacity. */
function isWithinBusinessHours(startMinutes: number): boolean {
  return (
    startMinutes >= SLOT_START_HOUR * MINUTES_PER_HOUR &&
    startMinutes < SLOT_END_HOUR * MINUTES_PER_HOUR
  );
}

export function isOpenSlot(
  buckets: Map<string, ClosingPill>,
  lane: GridLane,
  column: SlotColumn,
  nowMinutes: number | null = null
): boolean {
  if (lane.isUnassigned || !isWithinBusinessHours(column.startMinutes)) {
    return false;
  }

  const slotEndMinutes = column.startMinutes + SLOT_MINUTES;
  if (nowMinutes !== null && slotEndMinutes <= nowMinutes) {
    return false;
  }

  const occupant = buckets.get(bucketKey(lane.laneId, column.index));
  if (!occupant) {
    return true;
  }
  if (!isCancelledVariant(occupant.variant)) {
    return false;
  }
  const isCurrentSlot = nowMinutes !== null && column.startMinutes <= nowMinutes;
  return !isCurrentSlot;
}

/** Null rather than zero when no notary holds the day's work: capacity is unknown, not empty. */
export function openSlotCount(
  lanes: NotaryLane[],
  closings: ClosingPill[],
  now: Date | null = null
): number | null {
  const assigned = buildGridLanes(lanes, closings).filter(
    (lane) => !lane.isUnassigned
  );
  if (assigned.length === 0) {
    return null;
  }

  const axis = buildSlotAxis(closings);
  const buckets = bucketClosings(closings, axis);
  const nowMinutes = now ? minutesFromMidnight(now) : null;

  return assigned.reduce(
    (total, lane) =>
      total +
      axis.filter((column) => isOpenSlot(buckets, lane, column, nowMinutes))
        .length,
    0
  );
}

export function buildGridLanes(
  lanes: NotaryLane[],
  closings: ClosingPill[]
): GridLane[] {
  const alertLaneIds = new Set(
    closings
      .filter((closing) => isAlertVariant(closing.variant))
      .map((closing) => laneIdForClosing(closing))
  );

  const assigned: GridLane[] = lanes.map((lane) => ({
    laneId: lane.notaryId,
    name: lane.name,
    designation: lane.designation,
    status: lane.currentStatus,
    isUnassigned: false,
  }));

  const sorted = [...assigned].sort((left, right) => {
    const leftAlert = alertLaneIds.has(left.laneId) ? 0 : 1;
    const rightAlert = alertLaneIds.has(right.laneId) ? 0 : 1;
    return leftAlert - rightAlert;
  });

  const covered = new Set(sorted.map((lane) => lane.laneId));
  const uncovered = Array.from(
    new Set(
      closings
        .map((closing) => laneIdForClosing(closing))
        .filter((laneId) => !covered.has(laneId))
    )
  ).sort();

  uncovered.forEach((laneId) =>
    sorted.push({
      laneId,
      name: laneId === UNASSIGNED_LANE_ID ? UNASSIGNED_LANE_NAME : laneId,
      designation: null,
      status: null,
      isUnassigned: true,
    })
  );

  return sorted;
}

export function fittedSlotCount(
  slotSpace: number,
  minSlotWidth: number
): number {
  const fits = Math.floor(slotSpace / minSlotWidth);
  return Math.min(VISIBLE_SLOTS, Math.max(MIN_VISIBLE_SLOTS, fits));
}

export function clampWindowStart(
  start: number,
  totalSlots: number,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  const maxStart = Math.max(0, totalSlots - visibleSlots);
  if (start < 0) {
    return 0;
  }
  return start > maxStart ? maxStart : start;
}

export function centeredWindowStart(
  focusIndex: number | null,
  totalSlots: number,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  if (focusIndex === null) {
    return 0;
  }
  return clampWindowStart(
    focusIndex - Math.floor(visibleSlots / 2),
    totalSlots,
    visibleSlots
  );
}

export function initialWindowStart(
  now: Date | null,
  axis: SlotAxis,
  visibleSlots: number = VISIBLE_SLOTS
): number {
  if (!now || axis.length === 0) {
    return 0;
  }

  const focusIndex = currentSlotIndex(now, axis);
  if (focusIndex !== null) {
    return centeredWindowStart(focusIndex, axis.length, visibleSlots);
  }

  const minutes = minutesFromMidnight(now);
  if (minutes > axis[axis.length - 1].startMinutes) {
    return clampWindowStart(axis.length, axis.length, visibleSlots);
  }
  return 0;
}

export function slotPresentation(
  variant: SlotVariant,
  delayMinutes: number | null,
  icon: ClosingIcon | null
): SlotPresentation {
  const style = VARIANT_STYLES[variant];
  const label =
    style.showsDelay && delayMinutes !== null
      ? `${style.label} · ${delayMinutes} min`
      : style.label;
  const icons = icon ? [icon] : style.icons;

  return {
    variant,
    label,
    background: style.background,
    foreground: style.foreground,
    icons,
    iconNames: icons.map(iconSpriteName),
    renders: style.renders,
  };
}

export function openSlotPresentation(): SlotPresentation {
  return slotPresentation("open-slot", null, null);
}

export function emptyPresentation(): SlotPresentation {
  return slotPresentation("empty", null, null);
}

export function matchingLaneIds(
  term: string,
  lanes: GridLane[],
  closings: ClosingPill[]
): Set<string> {
  const needle = term.trim().toLowerCase();
  if (needle === "") {
    return new Set<string>();
  }

  const matches = new Set<string>();

  lanes.forEach((lane) => {
    if (lane.name.toLowerCase().includes(needle)) {
      matches.add(lane.laneId);
    }
  });

  closings.forEach((closing) => {
    if (isCancelledVariant(closing.variant)) {
      return;
    }
    if (
      closing.loanNumber.toLowerCase().includes(needle) ||
      closing.orderNumber.toLowerCase().includes(needle)
    ) {
      matches.add(laneIdForClosing(closing));
    }
  });

  return matches;
}

export function formatClockTime(value: Date): string {
  const minutes = minutesFromMidnight(value);
  const meridiem = minutes >= NOON_MINUTES ? "pm" : "am";
  return `${formatSlotLabel(minutes)} ${meridiem}`;
}

const MONTH_NAMES: readonly string[] = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export type DayKind = "past" | "today" | "future";

export function laneStatusFor(
  owned: ClosingPill[],
  dayKind: DayKind
): NotaryLaneStatus {
  const has = (variant: SlotVariant): boolean =>
    owned.some((pill) => pill.variant === variant);

  if (has("stuck")) {
    return { label: "Stuck", tagVariant: "error" };
  }
  if (has("delayed") || has("over")) {
    return { label: "Running behind", tagVariant: "warning" };
  }
  if (has("in-session")) {
    return { label: "In session", tagVariant: "info" };
  }
  if (has("check-in") || has("pre-sign")) {
    return { label: "Checking in", tagVariant: "info" };
  }
  if (dayKind === "past") {
    return { label: "Day complete", tagVariant: "success" };
  }
  if (dayKind === "future") {
    return { label: "Scheduled", tagVariant: "slate" };
  }
  return { label: "Waiting", tagVariant: "default" };
}

export function dayKindFor(date: Date, now: Date): DayKind {
  if (isSameDay(date, now)) {
    return "today";
  }
  return date.getTime() < now.getTime() ? "past" : "future";
}

export function isToday(dayKind: DayKind): boolean {
  return dayKind === "today";
}

export function toDateKey(date: Date): string {
  const wallClock = toBoardWallClock(date);
  const month = `${wallClock.getMonth() + 1}`.padStart(2, "0");
  const day = `${wallClock.getDate()}`.padStart(2, "0");
  return `${wallClock.getFullYear()}-${month}-${day}`;
}

export function isSameDay(left: Date, right: Date): boolean {
  const leftWallClock = toBoardWallClock(left);
  const rightWallClock = toBoardWallClock(right);
  return (
    leftWallClock.getFullYear() === rightWallClock.getFullYear() &&
    leftWallClock.getMonth() === rightWallClock.getMonth() &&
    leftWallClock.getDate() === rightWallClock.getDate()
  );
}

export function formatTimeRelativeTo(value: Date, reference: Date): string {
  const time = formatClockTime(value);
  if (isSameDay(value, reference)) {
    return time;
  }
  const wallClock = toBoardWallClock(value);
  return `${MONTH_NAMES[wallClock.getMonth()]} ${wallClock.getDate()}, ${time}`;
}

export type ClosingStatusClass =
  | "active"
  | "scheduled"
  | "canceled"
  | "completed"
  | "none";

export interface PanelAction {
  label: string;
  icon: string | null;
  disabled: boolean;
}

const ACTIVE_VARIANTS: readonly SlotVariant[] = [
  "over",
  "delayed",
  "stuck",
  "in-session",
  "pre-sign",
  "check-in",
];

export function closingStatusClass(variant: SlotVariant): ClosingStatusClass {
  if (ACTIVE_VARIANTS.includes(variant)) {
    return "active";
  }
  if (variant === "scheduled") {
    return "scheduled";
  }
  if (isCancelledVariant(variant)) {
    return "canceled";
  }
  if (variant === "completed" || variant === "processing-documents") {
    return "completed";
  }
  return "none";
}

const PANEL_ACTIONS: Readonly<
  Record<ClosingStatusClass, readonly PanelAction[]>
> = {
  active: [
    { label: "Call notary", icon: "phone", disabled: false },
    { label: "Text notary", icon: "chat", disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  scheduled: [
    { label: "Contact participants", icon: null, disabled: false },
    { label: "View closing details", icon: null, disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  canceled: [
    { label: "Reschedule appointment", icon: null, disabled: false },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  completed: [
    { label: "View closing details", icon: null, disabled: true },
    { label: "View completion summary", icon: null, disabled: true },
    { label: "Dispatch floater notary", icon: null, disabled: false },
  ],
  none: [],
};

export function panelActions(variant: SlotVariant): PanelAction[] {
  return PANEL_ACTIONS[closingStatusClass(variant)].map((action) => ({
    ...action,
  }));
}
